import { Check, ChevronDown, Loader2, Plus } from 'lucide-react'
import { AuthoredIcon } from '@/components/admin/shared/authored-icon'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { AddSourceModal } from '../add-source-modal'
import type { ProblemEditorData } from '../problem-editor'
import type { Source } from '../types'

// Difficulty name options for the form (sent as strings to the API)
const DIFFICULTY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'Easy', label: 'Easy' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Hard', label: 'Hard' },
]

interface SourceOption {
  id: string | null
  name: string
  icon: string | null
}

interface SelectedSourceInfo {
  name: string
  icon: string | null
}

export interface NewProblemSourceFormProps {
  formData: ProblemEditorData
  onFormUpdate: (updates: Partial<ProblemEditorData>) => void
  selectedSourceType: string
  setSelectedSourceType: (type: string) => void
  selectedSourceInfo: SelectedSourceInfo | undefined
  allSourcesForDropdown: SourceOption[]
  loadingAvailableSources: boolean
  shouldShowHighestId: boolean
  loadingHighestId: boolean
  /** Highest purely numeric ID (e.g., "123") */
  highestNumericId: string | null
  /** Highest alphanumeric ID (e.g., "CH10") */
  highestAlphanumericId: string | null
  addSourceModalOpen: boolean
  setAddSourceModalOpen: (open: boolean) => void
  onSourceCreated: (source: Source) => void
}

export function NewProblemSourceForm({
  formData,
  onFormUpdate,
  selectedSourceType,
  setSelectedSourceType,
  selectedSourceInfo,
  allSourcesForDropdown,
  loadingAvailableSources,
  shouldShowHighestId,
  loadingHighestId,
  highestNumericId,
  highestAlphanumericId,
  addSourceModalOpen,
  setAddSourceModalOpen,
  onSourceCreated,
}: NewProblemSourceFormProps) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Primary Source</h3>
        <p className="text-muted-foreground text-sm">
          Set up the initial source for this problem.
        </p>
      </div>

      <div className="rounded-lg border p-4">
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Source Platform */}
          <div>
            <label
              htmlFor="new-source-platform"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Source Platform <span className="text-destructive">*</span>
            </label>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-9 flex-1 justify-between"
                    disabled={loadingAvailableSources}
                  >
                    <span className="flex items-center gap-2">
                      {loadingAvailableSources ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-muted-foreground">
                            Loading sources...
                          </span>
                        </>
                      ) : (
                        <>
                          {selectedSourceInfo?.icon && (
                            <AuthoredIcon
                              icon={selectedSourceInfo.icon}
                              className="shrink-0"
                              fallbackChip
                            />
                          )}
                          <span className="truncate">{selectedSourceType}</span>
                        </>
                      )}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[300px]">
                  {allSourcesForDropdown.map((s) => (
                    <DropdownMenuItem
                      key={s.id || s.name}
                      onSelect={() => {
                        setSelectedSourceType(s.name)
                        onFormUpdate({
                          source: s.name,
                          sourceId: s.id,
                          sourceIcon: s.icon || null,
                        })
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="flex items-center gap-2">
                          <AuthoredIcon icon={s.icon} fallbackChip />
                          <span>{s.name}</span>
                        </span>
                        {selectedSourceType === s.name && (
                          <Check className="h-4 w-4 text-green-500" />
                        )}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setAddSourceModalOpen(true)}
                className="h-9"
                title="Add new source"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* External ID */}
          <div>
            <label
              htmlFor="new-external-id"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              External ID
              {shouldShowHighestId && (
                <span className="text-muted-foreground ml-2 font-normal">
                  {loadingHighestId ? (
                    '(Loading...)'
                  ) : highestNumericId || highestAlphanumericId ? (
                    <span className="text-primary">
                      (Highest:{' '}
                      {highestNumericId && highestAlphanumericId ? (
                        // Both types exist - show both
                        <>
                          {highestNumericId}{' '}
                          <span className="text-muted-foreground">|</span>{' '}
                          {highestAlphanumericId}
                        </>
                      ) : (
                        // Only one type exists
                        highestNumericId || highestAlphanumericId
                      )}
                      )
                    </span>
                  ) : (
                    <span className="text-muted-foreground/70">
                      (No existing IDs)
                    </span>
                  )}
                </span>
              )}
            </label>
            <Input
              id="new-external-id"
              type="text"
              value={formData.externalId || ''}
              onChange={(e) => onFormUpdate({ externalId: e.target.value })}
              placeholder="e.g., 1 or CH1-Q3"
              className="font-mono"
            />
          </div>

          {/* Problem Name/Title */}
          <div className="sm:col-span-2">
            <label
              htmlFor="new-problem-name"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Problem Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="new-problem-name"
              type="text"
              value={formData.name}
              onChange={(e) => onFormUpdate({ name: e.target.value })}
              placeholder="Problem name (e.g., Two Sum)"
              required
            />
          </div>

          {/* Difficulty */}
          <div>
            <label
              htmlFor="new-difficulty"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Difficulty
            </label>
            <select
              id="new-difficulty"
              value={formData.difficulty || ''}
              onChange={(e) =>
                onFormUpdate({
                  difficulty: e.target.value || null,
                })
              }
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              {DIFFICULTY_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          {/* Premium */}
          <div className="flex items-center gap-2 pt-6">
            <input
              id="new-premium"
              type="checkbox"
              checked={formData.premiumRequired || false}
              onChange={(e) =>
                onFormUpdate({ premiumRequired: e.target.checked })
              }
              className="rounded"
            />
            <label htmlFor="new-premium" className="text-sm">
              Premium Required
            </label>
          </div>

          {/* Problem URL */}
          <div>
            <label
              htmlFor="new-url"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Problem URL
            </label>
            <Input
              id="new-url"
              type="url"
              value={formData.url || ''}
              onChange={(e) => onFormUpdate({ url: e.target.value || null })}
              placeholder="https://leetcode.com/problems/..."
            />
          </div>

          {/* Editorial URL */}
          <div>
            <label
              htmlFor="new-editorial"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Editorial URL
            </label>
            <Input
              id="new-editorial"
              type="url"
              value={formData.editorialUrl || ''}
              onChange={(e) =>
                onFormUpdate({ editorialUrl: e.target.value || null })
              }
              placeholder="https://..."
            />
          </div>
        </div>
      </div>

      {/* Add Source Modal */}
      <AddSourceModal
        isOpen={addSourceModalOpen}
        onClose={() => setAddSourceModalOpen(false)}
        onSourceCreated={onSourceCreated}
      />
    </div>
  )
}
